import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {ModalType} from "data/enums";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {first} from "lodash";
import {DateTime} from "luxon";
import {GAME_TIMEZONE} from "data/constants";
import {useNavigate} from "react-router-dom";

interface IParams {
	navigate: ReturnType<typeof useNavigate>;
}
export interface ITomorrowNavigationController extends ViewController<IParams> {
	i18n: ILocalizationStore;
	showDayPicker: () => void;
	showInfoModal: () => void;

	get contestDay(): string;
}

@injectable()
export class TomorrowNavigationController implements ITomorrowNavigationController {
	private _navigate?: IParams["navigate"];
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.RosterStore) private _rosterStore: IRosterStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get contestDay(): string {
		const currentContest = this._contestsStore.getContestById(
			this._rosterStore.currentContestId
		);
		if (!currentContest) {
			return "-";
		}

		const contestDate = DateTime.fromISO(currentContest.date).setZone(GAME_TIMEZONE);
		return contestDate.isValid ? contestDate.toFormat("L/dd") : "-";
	}

	public showDayPicker = () => {
		this._modalsStore.showModal(ModalType.DAY_PICKER, {
			meta: {
				type: "picker",
				lockByContests: this.playedContests,
				onSelect: (contestId: number) => {
					this._navigate?.("../");
					this._modalsStore.hideModal();
				},
			},
		});
	};

	get playedContests() {
		const rosterPlayed = this._rosterStore.playedContests || [];
		const currentContest = first(this._contestsStore.scheduledContests);

		if (!currentContest) {
			return rosterPlayed;
		}

		return [...rosterPlayed, currentContest.id];
	}

	showInfoModal = () => {
		this._modalsStore.showModal(ModalType.TOMORROW_GAME);
	};

	init(param: IParams) {
		this._navigate = param.navigate;
	}

	onChange(param: IParams) {
		this.init(param);
	}
}
